import React from "react";
import {FloatingFocusManager, FloatingPortal} from "@floating-ui/react";
import useFloatingMenu from "../merchant-portals-menu/portal-select/useFloatingMenu";

import "./header-menu.scss";
import {observer} from "mobx-react";

export enum HEADER_MENU_VARIANT {
    HELP_CENTER_MENU = 'help-center-menu',
    PERSONAL_AREA_MENU = 'personal-area-menu',
    PORTAL_SELECT_MENU = 'portal-select-menu',
};

type HeaderMenuProps = {
    toggleContents: ((arg: { isOpen: boolean }) => React.ReactNode) | React.ReactNode;
    className?: string;
    closeOnClick?: boolean;
    children:  ((arg: { setIsOpen: React.Dispatch<React.SetStateAction<boolean>> }) => React.ReactNode) | React.ReactNode;
    onToggle?: () => void;
    variant?: HEADER_MENU_VARIANT;
};

const HeaderMenu: React.FC<HeaderMenuProps> = observer(({ children, toggleContents, className, onToggle, closeOnClick = true, variant }) => {
    const { isOpen, setIsOpen, refs, getReferenceProps, context, floatingStyles, getFloatingProps  } = useFloatingMenu(variant);
    /** The getReferenceProps can return a onClick function.
     * When we add our own onClick function to the div, we override it, therefore we need to spread the getReferenceProps onClick function as onClickFromReferenceProps,
     * and call it in our onClick function.
    */
    const { onClick: onClickFromReferenceProps, ...rest } = getReferenceProps();

    return (
        <div className={`header-menu ${className ? className : ''}`}>
            <div
                ref={refs.setReference}
                {...rest}
                className="header-menu__toggle-contents"
                onClick={(e) => {
                    if (onToggle) {
                        onToggle();
                    }
                    if (typeof onClickFromReferenceProps === 'function') {
                        onClickFromReferenceProps(e);
                    }
                }}
            >
                {typeof toggleContents === 'function' ? toggleContents({ isOpen }) : toggleContents}
            </div>

            <FloatingPortal>
                {isOpen && (
                    <FloatingFocusManager context={context} modal={false} initialFocus={variant === HEADER_MENU_VARIANT.PORTAL_SELECT_MENU ? -1 : undefined}>
                        <div
                            ref={refs.setFloating}
                            style={floatingStyles}
                            {...getFloatingProps()}
                            className={`header-menu__floating-portal-contents ${variant ? `header-menu__floating-portal-contents--${variant}` : ''}`}
                            onClick={() => closeOnClick && setIsOpen(false)}
                        >
                            {typeof children === 'function' ? children({ setIsOpen }) : children}
                        </div>
                    </FloatingFocusManager>
                )}
            </FloatingPortal>
        </div>
    );
})

export const HeaderMenuOption: React.FC<{ children: React.ReactNode, onClick?: () => void }> = ({ children, onClick }) => {
    return (
        <div className="header-menu__floating-portal-contents__option" onClick={onClick}>{children}</div>
    );
};

export default HeaderMenu;